import { useEffect, useState } from "react";
import { Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Axios from "services/api/config";
import { getStaffURL, revokeHelpDeskURL } from "services/api/routes/Staff";

/**
 * Who has help-desk access. Access is given by ticking a customer on the Customer
 * list; they sign in to this dashboard with their own Styola email + password and
 * see only the help desk (tickets with order details and the courier timeline).
 */
function Staff() {
	const [staff, setStaff] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();

	const load = async () => {
		setLoading(true);
		try {
			const { data } = await Axios({ ...getStaffURL });
			setStaff(data?.data || []);
		} catch (e) {
			toast.error("Could not load help-desk access");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		load();
	}, []);

	const remove = async (s: any) => {
		if (!window.confirm(`Remove help-desk access for ${s.email}?`)) return;
		try {
			await Axios({ ...revokeHelpDeskURL(s.customerId) });
			toast.success("Help-desk access removed");
			load();
		} catch (e) {
			toast.error("Could not remove help-desk access");
		}
	};

	return (
		<Box sx={{ p: { xs: 1, md: 2 } }}>
			<Typography className="!text-2xl !font-semibold !mb-1">Help-desk access</Typography>
			<Typography className="!text-sm !text-gray-500 !mb-4">
				To give someone access, tick <b>Help desk</b> next to them on the{" "}
				<Button size="small" sx={{ textTransform: "none", p: 0, minWidth: 0, verticalAlign: "baseline" }} onClick={() => navigate("/app/customer")}>
					Customer list
				</Button>
				. They sign in here with their own Styola email and password (plus the email code) and see only the help desk:
				tickets with the customer&apos;s name, phone, address, order status and courier timeline. They can reply and
				resolve tickets, but cannot approve returns or refunds, or change orders, payments, products or coupons.
			</Typography>
			<Card sx={{ p: 2 }}>
				{loading ? (
					<CircularProgress size={24} />
				) : staff.length === 0 ? (
					<Typography className="!text-sm !text-gray-500">Nobody has help-desk access yet.</Typography>
				) : (
					staff.map((s) => (
						<Box key={s.id} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1, borderTop: "1px solid #eee" }}>
							<Box sx={{ flex: 1 }}>
								<Typography className="!text-sm !font-medium">{s.name}</Typography>
								<Typography className="!text-xs !text-gray-500">{s.email}</Typography>
							</Box>
							<Button size="small" color="error" variant="outlined" onClick={() => remove(s)} sx={{ textTransform: "none" }}>
								Remove access
							</Button>
						</Box>
					))
				)}
			</Card>
		</Box>
	);
}

export default Staff;
